import { displayOdds, ladderLineSummaries, meetsMinStake, otherLines, type LadderLineSummary } from "@/lib/odds-screen/live/derive";
import { ladderLineId, type BookId, type EntityKey, type EventId, type EventHeader, type FamilyId, type Ladder, type LeagueId, type MarketSpec, type OddsDisplay } from "@/lib/odds-screen/live/types";
import { cellLineLabel, outcomeDisplayOrder, outcomeName } from "./labels";

export type DrillTarget = {
  readonly league: LeagueId;
  readonly event: EventId;
  readonly family: FamilyId;
  readonly entity: EntityKey;
  readonly slot: number;
  readonly outcome: number;
  readonly line: number | null;
};

export type RailBookRow = {
  readonly book: BookId;
  readonly decimal: number;
  readonly display: string;
  readonly link: string | null;
  readonly appLink: string | null;
  readonly stake: number | null;
  readonly belowMin: boolean;
  readonly best: boolean;
  readonly selectionId: string | null;
  readonly side: string | null;
  readonly held: boolean;
  readonly bettable: boolean;
};

export type RailOtherRow = {
  readonly book: BookId;
  readonly lineLabel: string;
  readonly display: string;
  readonly link: string | null;
  readonly appLink: string | null;
  readonly belowMin: boolean;
  readonly bettable: boolean;
};

export type RailColumn = {
  readonly outcome: number;
  readonly label: string;
  readonly rows: readonly RailBookRow[];
  readonly others: readonly RailOtherRow[];
};

export type RailModel = {
  readonly summaries: readonly LadderLineSummary[];
  readonly lineValue: number | null;
  readonly columns: readonly RailColumn[];
};

function order(a: { decimal: number; book: BookId; held: boolean }, b: { decimal: number; book: BookId; held: boolean }): number {
  if (a.held !== b.held) return a.held ? 1 : -1;
  if (a.decimal !== b.decimal) return b.decimal - a.decimal;
  return a.book < b.book ? -1 : a.book > b.book ? 1 : 0;
}

export function railModel(
  ladder: Ladder,
  spec: MarketSpec,
  slot: number,
  line: number | null,
  event: EventHeader | undefined,
  selected: ReadonlySet<BookId>,
  listed: ReadonlySet<BookId>,
  format: OddsDisplay,
  minStake: number,
): RailModel {
  const summaries = ladderLineSummaries(ladder, slot, listed);
  const resolvedLine = line ?? ladder.mains[slot] ?? summaries.find((entry) => entry.isMain)?.line ?? summaries[0]?.line ?? null;
  const chosen = resolvedLine === null ? undefined : ladder.lines.get(ladderLineId(slot, resolvedLine));

  const columns = outcomeDisplayOrder(spec).map((outcome): RailColumn => {
    const rows: RailBookRow[] = [];
    for (const book of chosen?.books ?? []) {
      if (!listed.has(book.book)) continue;
      const cell = book.outcomes[outcome];
      if (cell === null || cell === undefined) continue;
      const bettable = selected.has(book.book);
      rows.push({ book: book.book, decimal: cell.odds, display: displayOdds(cell.odds, format), link: bettable ? cell.link : null, appLink: bettable ? cell.appLink : null, stake: cell.stake, belowMin: !meetsMinStake(cell.stake, minStake), best: false, selectionId: cell.selectionId, side: cell.side, held: cell.held === true, bettable });
    }
    rows.sort(order);
    const pickable = (row: RailBookRow) => row.bettable && !row.held && !row.belowMin;
    const topDisplay = rows.find(pickable)?.display;
    const ranked = rows.map((row) => ({ ...row, best: pickable(row) && row.display === topDisplay }));

    const others = resolvedLine === null
      ? []
      : otherLines(ladder, slot, resolvedLine, listed)
          .flatMap((other): RailOtherRow[] => {
            const cell = other.outcomes[outcome];
            if (cell === null || cell === undefined || cell.held) return [];
            const bettable = selected.has(other.book);
            return [{ book: other.book, lineLabel: cellLineLabel(spec, other.line, outcome), display: displayOdds(cell.odds, format), link: bettable ? cell.link : null, appLink: bettable ? cell.appLink : null, belowMin: !meetsMinStake(cell.stake, minStake), bettable }];
          });

    return { outcome, label: outcomeSideName(spec, resolvedLine, event, outcome), rows: ranked, others };
  });

  return { summaries, lineValue: resolvedLine, columns };
}

function outcomeSideName(spec: MarketSpec, line: number | null, event: EventHeader | undefined, outcome: number): string {
  if (spec.line === "total") return spec.outcomes[outcome] === "under" ? "Under" : "Over";
  if (spec.line === "spread" && line !== null) {
    const value = spec.outcomes[outcome] === "away" ? -line : line;
    return `${outcomeName(spec, event, outcome)} ${value === 0 ? "PK" : value > 0 ? `+${value}` : value}`;
  }
  return outcomeName(spec, event, outcome);
}
